import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
  PropsWithChildren,
} from "react";

/**
 * AutoLogoutInfoContext
 *
 * Purpose: Controls whether to display the "Your session has expired" message on login pages
 *
 * Used by:
 * - PageAdminLogin (Admin login at /admin)
 * - PageWelcome (RE login at /admission/welcome)
 *
 * Behavior:
 * 1. When auto-logout happens → flag set to true → both login pages show info bar
 * 2. When user logs back in → flag cleared → info bar disappears
 * 3. Broadcast Channel syncs flag across tabs (if multiple login tabs open)
 *
 * Note: This context does NOT control timeout logic - only the UI notification.
 * Actual timeout is managed by AutoLogoutContext (wraps authenticated routes).
 */

interface AutoLogoutInfoContextValue {
  isAutoLoggedOut: boolean;
  setAutoLoggedOut: () => void;
  clearAutoLoggedOut: () => void;
}

const AutoLogoutInfoContext = createContext<AutoLogoutInfoContextValue | null>(
  null,
);

export function AutoLogoutInfoProvider({children}: PropsWithChildren<{}>) {
  const [isAutoLoggedOut, setIsAutoLoggedOut] = useState(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Initialize persistent channel once
  useEffect(() => {
    channelRef.current = new BroadcastChannel("auto-logout-info");

    const handleMessage = (event: MessageEvent<"logged_out" | "logged_in">) => {
      setIsAutoLoggedOut(event.data === "logged_out");
    };

    channelRef.current.addEventListener("message", handleMessage);

    return () => {
      channelRef.current?.removeEventListener("message", handleMessage);
      channelRef.current?.close();
    };
  }, []);

  const setAutoLoggedOut = useCallback(() => {
    setIsAutoLoggedOut(true);
    channelRef.current?.postMessage("logged_out");
  }, []);

  const clearAutoLoggedOut = useCallback(() => {
    setIsAutoLoggedOut(false);
    channelRef.current?.postMessage("logged_in");
  }, []);

  return (
    <AutoLogoutInfoContext.Provider
      value={{isAutoLoggedOut, setAutoLoggedOut, clearAutoLoggedOut}}
    >
      {children}
    </AutoLogoutInfoContext.Provider>
  );
}

export const useAutoLogoutInfo = (): AutoLogoutInfoContextValue => {
  const context = useContext(AutoLogoutInfoContext);
  if (!context) {
    throw new Error(
      "useAutoLogoutInfo must be used within AutoLogoutInfoProvider",
    );
  }
  return context;
};
